import React, { useState } from 'react';
import { Mail, Phone, Send, X } from 'lucide-react'; // Added X for close button
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({ open: false, type: '', title: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      // Show success modal
      setModal({
        open: true,
        type: 'success',
        title: 'Message Sent Successfully! 🎉',
        message: response.data.message || "Thank you! We'll get back to you soon."
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);

      let errorMessage = "Failed to send message. Please try again later.";

      if (error.response?.status === 422 && error.response.data.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join(' ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Show error modal
      setModal({
        open: true,
        type: 'error',
        title: 'Submission Failed',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModal({ open: false, type: '', title: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen relative"> {/* relative for modal overlay */}
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        {/* ... (same as before) */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 md:w-80 h-40 md:h-80 bg-[#0052CC]/20 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            Get In Touch
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Let's{' '}
            <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
              Connect
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Cards (same as before) */}
            <div className="space-y-6">
              {/* ... your two cards ... */}
              <Card className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-lg transition-all duration-300">
                <CardContent className="pt-6 text-center md:text-left">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-lg flex items-center justify-center text-white mb-4 mx-auto md:mx-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Email Us</h3>
                  <p className="text-gray-600 mb-2">Our team is here to help</p>
                  <a href="mailto:support@hrmqore.com" className="text-[#0066FF] hover:underline">
                    support@hrmqore.com
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-lg transition-all duration-300">
                <CardContent className="pt-6 text-center md:text-left">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-lg flex items-center justify-center text-white mb-4 mx-auto md:mx-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Call Us</h3>
                  <p className="text-gray-600 mb-2">Mon-Fri from 8am to 6pm</p>
                  <a href="tel:+2347042050336" className="text-[#0066FF] hover:underline">
                    07042050336
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-gray-100 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6 text-center md:text-left">Send us a message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* ... all your form fields (same as before) ... */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <Input name="name" required placeholder="John Doe" value={formData.name} onChange={handleChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <Input name="email" type="email" required placeholder="john@company.com" value={formData.email} onChange={handleChange} />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                        <Input name="company" placeholder="Your Company" value={formData.company} onChange={handleChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <Input name="phone" type="tel" placeholder="+1 (555) 123-4567" value={formData.phone} onChange={handleChange} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <Input name="subject" required placeholder="How can we help you?" value={formData.subject} onChange={handleChange} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <Textarea name="message" required placeholder="Tell us more about your inquiry..." rows={6} value={formData.message} onChange={handleChange} />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Centered Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in duration-300">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              {modal.type === 'success' ? (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}

              <h3 className="text-2xl font-bold mb-3">{modal.title}</h3>
              <p className="text-gray-600 mb-8">{modal.message}</p>

              <Button
                onClick={closeModal}
                className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;